// Shared logic for the Network Devices (RADIUS) screens.
//
// A "device" is a row of did.network_devices (PAM's table): a VPN, switch, router,
// firewall or Wi-Fi controller that sends RADIUS logins to the customer's FreeRADIUS.
// Rows come from three places -- the old agent, the backend registering a device the
// first time it sends a login, and the Add Radius Device form -- so this file smooths
// over their differences.

export const DEVICE_TYPES = ["VPN", "Router", "Switch", "Firewall", "Wi-Fi", "Other"];

// The old agent stored the device's name in device_type and left name empty; newer rows
// use name for the name and device_type for the kind of device.
export function deviceDisplayName(d) {
  return String(d?.name || d?.device_type || d?.nas_identifier || d?.ip_address || "").trim();
}

export function deviceTypeLabel(d) {
  return d?.name ? String(d?.device_type || "").trim() : "";
}

// The Type column: one of DEVICE_TYPES (matched case-insensitively, so an old "router" reads
// "Router"), anything else as stored, "Not set" when empty.
export function deviceTypeText(d) {
  const t = deviceTypeLabel(d);
  if (!t) return "Not set";
  return DEVICE_TYPES.find((x) => x.toLowerCase() === t.toLowerCase()) || t;
}

// DeviceActivity.status (computed by the API) → the pill on the Network Devices page.
// Discovered: registered itself on its first login and no admin has named it yet. Active: a
// login in the last 30 days. Inactive: none in 30 days.
export const DEVICE_STATUS = {
  active: { label: "Active", tone: "success" },
  discovered: { label: "Discovered", tone: "info" },
  inactive: { label: "Inactive", tone: "neutral" },
};

export function deviceStatusView(activity) {
  return DEVICE_STATUS[String(activity?.status || "").toLowerCase()] || null;
}

// What a policy's "Network devices" field should contain to target this device. The
// backend matches NAS-Identifier first, then address.
export function devicePolicyValue(d) {
  return String(d?.nas_identifier || d?.ip_address || "").trim();
}

// ─── Which policies apply to a device ──────────────────────────────────────────
// Mirrors matchRadiusClient in authnull-service internal/radius/matcher.go: an empty
// list or "*" covers every device; otherwise an entry matches the NAS-Identifier
// (case-insensitive), the exact address, or a CIDR containing the address.

function cleanList(list) {
  return (Array.isArray(list) ? list : []).map((t) => String(t || "").trim()).filter(Boolean);
}

function ipv4ToInt(ip) {
  const parts = String(ip || "").trim().split(".");
  if (parts.length !== 4) return null;
  let n = 0;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p) || Number(p) > 255) return null;
    n = n * 256 + Number(p);
  }
  return n;
}

// ponytail: IPv4 only -- an IPv6 CIDR entry is not counted here (the backend still
// enforces it); add IPv6 parsing if customers scope policies by IPv6 range.
export function ipv4InCidr(ip, cidr) {
  const [base, bitsStr] = String(cidr || "").split("/");
  const bits = Number(bitsStr);
  const ipN = ipv4ToInt(ip);
  const baseN = ipv4ToInt(base);
  if (ipN === null || baseN === null || !/^\d{1,2}$/.test(String(bitsStr)) || bits > 32) return false;
  if (bits === 0) return true;
  const block = 2 ** (32 - bits);
  return Math.floor(ipN / block) === Math.floor(baseN / block);
}

// "this" = the policy names this device; "all" = the policy covers every device;
// null = the policy is scoped to other devices.
export function policyDeviceScope(policy, device) {
  const clients = cleanList(policy?.policyJson?.radius?.clients);
  if (!clients.length || clients.includes("*")) return "all";
  const nasId = String(device?.nas_identifier || "").trim().toLowerCase();
  const ips = [device?.ip_address, device?.client_ip].map((v) => String(v || "").trim()).filter(Boolean);
  for (const c of clients) {
    if (nasId && c.toLowerCase() === nasId) return "this";
    if (ips.includes(c)) return "this";
    if (c.includes("/") && ips.some((ip) => ipv4InCidr(ip, c))) return "this";
  }
  return null;
}

export function countPoliciesNamingDevice(policies, device) {
  return (policies || []).filter((p) => policyDeviceScope(p, device) === "this").length;
}

// ─── Adding a device ───────────────────────────────────────────────────────────

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

export function isValidDeviceIp(ip) {
  const v = String(ip || "").trim();
  return IPV4.test(v) || (/^[0-9a-fA-F:]+$/.test(v) && v.includes(":"));
}

// A shared secret for clients.conf. Letters and digits only, so it can be written
// unquoted and never needs escaping.
export function generateSharedSecret(length = 32) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint32Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

function clientBlockName(name) {
  const slug = String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug || "device";
}

// ─── RADIUS log ────────────────────────────────────────────────────────────────

// authn-service writes a RADIUS log row's reason as "<outcome> · <policy>" -- the policy only
// when one decided. Returns [outcome, policy].
export function splitReason(value) {
  const s = String(value || "");
  const i = s.lastIndexOf(" · ");
  return i < 0 ? [s, ""] : [s.slice(0, i), s.slice(i + 3)];
}

// The log API counts logins per 15-minute bucket and sends only the buckets that have logins, so a
// chart of them draws a line straight across the hours in between, as if logins happened there.
// Filling the empty buckets with 0 (plus one on each side, so a lone bucket shows as a spike) makes
// the chart show when logins actually happened.
// ponytail: capped at 2000 buckets (~3 weeks); a wider range is plotted as sent -- aggregate per
// day (utils/authLog dailyTimeline) if long ranges matter.
export const TIMELINE_BUCKET_MS = 900000;

export function zeroFillTimeline(timeline, stepMs = TIMELINE_BUCKET_MS, maxBuckets = 2000) {
  const pts = (timeline || [])
    .map((b) => ({ tsUnixMs: Number(b.tsUnixMs), count: Number(b.count) || 0 }))
    .filter((b) => Number.isFinite(b.tsUnixMs))
    .sort((a, b) => a.tsUnixMs - b.tsUnixMs);
  if (!pts.length) return [];
  const first = pts[0].tsUnixMs - stepMs;
  const last = pts[pts.length - 1].tsUnixMs + stepMs;
  if ((last - first) / stepMs + 1 > maxBuckets) return pts;
  const counts = new Map(pts.map((p) => [p.tsUnixMs, p.count]));
  const out = [];
  for (let t = first; t <= last; t += stepMs) out.push({ tsUnixMs: t, count: counts.get(t) || 0 });
  return out;
}

// ─── Add RADIUS server (was: Connect FreeRADIUS) ──────────────────────────────

// Public repo, branch "radius". The script downloads the bridge, checks its checksum and
// installs it into FreeRADIUS; given app.env it also installs that as the bridge config.
export const BRIDGE_INSTALL_URL = "https://github.com/authnull0/windows-endpoint/raw/radius/agent/radius-bridge/install.sh";
// OLD: export const BRIDGE_INSTALL_COMMAND = `curl -fsSL ${BRIDGE_INSTALL_URL} | sudo bash -s -- ./app.env`;
// --ad: after installing the bridge, install.sh also connects FreeRADIUS to Active Directory and
// tests a login (it asks for the domain controller and a service account on the terminal).
export const BRIDGE_INSTALL_COMMAND = `curl -fsSL ${BRIDGE_INSTALL_URL} | sudo bash -s -- ./app.env --ad`;

// authn-service publishes 2882 on the docker host, which on-prem is the host serving
// this console. Only a default: the admin can change it on the page.
export function defaultBridgeApiUrl(hostname) {
  return hostname ? `http://${hostname}:2882` : "";
}

// app.env for the bridge (/etc/authnull/radius-bridge.env). API_BASE_URL, ORG_ID and TENANT_ID
// are required by the bridge and have no defaults there. RADIUS_SERVER_ID is this server's id on
// the RADIUS Servers page: the heartbeat needs it, and every login carries it so the log shows
// which server handled it. DOMAIN is optional (empty: each user's own directory).
// OLD: export function buildBridgeEnvFile({ apiBaseUrl, orgId, tenantId, domain }) {
export function buildBridgeEnvFile({ apiBaseUrl, orgId, tenantId, domain, radiusServerId }) {
  return [
    "# AuthNull RADIUS bridge configuration",
    "# Installed to /etc/authnull/radius-bridge.env by install.sh",
    "",
    `API_BASE_URL=${String(apiBaseUrl || "").trim().replace(/\/+$/, "")}`,
    `ORG_ID=${orgId ?? ""}`,
    `TENANT_ID=${tenantId ?? ""}`,
    `RADIUS_SERVER_ID=${String(radiusServerId || "").trim()}`,
    `DOMAIN=${String(domain || "").trim()}`,
    "",
    "MFA_TIMEOUT_SEC=60",
    // "POLL_INTERVAL_SEC=2", -- retired in the bridge: nothing polls since the decision is one
    // blocking call (older bridges default it to 2, so leaving it out is safe for them too).
    "FAIL_OPEN=false",
    "LOG_PATH=/var/log/authnull/radius-bridge.log",
    "",
  ].join("\n");
}

// Which RADIUS client the device is, for the BlastRADIUS lines of its clients.conf block
// (P2-1). Not saved: it only shapes the block shown once when the device is added.
// OLD (radius-hardening): a fourth kind, "prompt" (FortiGate / Palo Alto / Cisco, "can show an
// Enter code box"), also wrote authnull_prompt = yes. Typed codes are not in this release, so
// those devices take the sends-Message-Authenticator lines and no prompt.
export const RADIUS_CLIENT_KINDS = [
  { value: "unknown", label: "Not sure" },
  { value: "openvpn", label: "OpenVPN (RADIUS plugin)" },
  { value: "sends-ma", label: "strongSwan, FortiGate, Palo Alto, Cisco, or another device that sends Message-Authenticator" },
];

// The block the admin pastes into /etc/freeradius/3.0/clients.conf.
// OLD: export function clientsConfSnippet({ name, ip, secret }) {
export function clientsConfSnippet({ name, ip, secret, kind = "unknown" }) {
  // BlastRADIUS. A device that sends Message-Authenticator is made to send it. One that
  // cannot -- OpenVPN's radiusplugin -- gets limit_proxy_state instead, which refuses the
  // attack's Proxy-State without one. require = yes on OpenVPN drops every request
  // (verified on FreeRADIUS 3.2.1: "does not contain required Message-Authenticator").
  // "Not sure" takes the OpenVPN lines: they work with every device.
  const sendsMessageAuthenticator = kind === "sends-ma";
  return [
    `client ${clientBlockName(name)} {`,
    `    ipaddr = ${String(ip || "").trim()}`,
    `    secret = ${secret}`,
    // `    require_message_authenticator = yes`,
    // Removed: OpenVPN's radiusplugin (and other older NAS) never send Message-Authenticator,
    // so FreeRADIUS dropped every request from them ("Insecure packet ... does not contain
    // required Message-Authenticator") and the login just timed out. Left to FreeRADIUS's
    // default (auto from 3.2.5, no before); the add panel says when to set it.
    ...(sendsMessageAuthenticator
      ? [`    require_message_authenticator = yes`]
      : [`    require_message_authenticator = no`, `    limit_proxy_state = yes`]),
    `}`,
  ].join("\n");
}
